import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatPct, parseDecimal } from "@/lib/format";
import { defaultPeriod } from "@/lib/periods";
import { PeriodFilter } from "@/components/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Target, Calculator } from "lucide-react";

// Pure client-side scenario math (mirrors backend finance_calc)
const breakeven = (fixed, imc) => (imc == null || imc <= 0 ? null : fixed / (imc / 100));
const requiredRevenue = (fixed, desired, imc) => (imc == null || imc <= 0 ? null : (fixed + desired) / (imc / 100));

function ScenarioCard({ name, testid }) {
  const [f, setF] = useState({ receita: "", imc: "", fixas: "", meta: "" });
  const rl = parseDecimal(f.receita) || 0;
  const imc = parseDecimal(f.imc);
  const fixas = parseDecimal(f.fixas) || 0;
  const meta = parseDecimal(f.meta) || 0;
  const mc = isNaN(imc) ? 0 : rl * (imc / 100);
  const resultado = mc - fixas;
  const pe = breakeven(fixas, isNaN(imc) ? null : imc);
  const req = requiredRevenue(fixas, meta, isNaN(imc) ? null : imc);
  const ms = pe == null ? null : rl - pe;
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-display font-semibold mb-3">{name}</h3>
      <div className="space-y-2">
        <div><Label className="text-xs">Receita projetada</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.receita} onChange={(e) => setF({ ...f, receita: e.target.value })} className="h-8" /></div>
        <div><Label className="text-xs">IMC %</Label><Input type="text" inputMode="decimal" placeholder="40" value={f.imc} onChange={(e) => setF({ ...f, imc: e.target.value })} className="h-8" /></div>
        <div><Label className="text-xs">Despesas fixas</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.fixas} onChange={(e) => setF({ ...f, fixas: e.target.value })} className="h-8" /></div>
        <div><Label className="text-xs">Meta de resultado</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.meta} onChange={(e) => setF({ ...f, meta: e.target.value })} className="h-8" /></div>
      </div>
      <div className="mt-4 space-y-2 text-sm border-t border-border pt-3">
        <div className="flex justify-between"><span className="text-muted-foreground">Ponto de equilíbrio</span><span className="font-num font-semibold">{pe == null ? "N/A" : formatBRL(pe)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Resultado esperado</span><span className={`font-num font-semibold ${resultado >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatBRL(resultado)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Faturamento p/ meta</span><span className="font-num font-semibold">{req == null ? "N/A" : formatBRL(req)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Margem de segurança</span><span className="font-num font-semibold">{ms == null ? "N/A" : formatBRL(ms)}</span></div>
      </div>
    </div>
  );
}

export default function Metas() {
  const { currentOrg } = useAuth();
  const [period, setPeriod] = useState(defaultPeriod());
  const [summary, setSummary] = useState(null);
  const [desired, setDesired] = useState("");
  const [reqResult, setReqResult] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [target, setTarget] = useState({ receita_liquida: "", imc: "", resultado_operacional: "" });
  const [periodStatus, setPeriodStatus] = useState("OPEN");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, pr] = await Promise.all([
        api.get("/finance/summary", { params: { date_from: period.from, date_to: period.to } }),
        api.get("/financial-targets", { params: { month } }),
        api.get("/financial-periods"),
      ]);
      setSummary(s.data);
      const tg = t.data[0];
      if (tg) setTarget({ receita_liquida: tg.receita_liquida ?? "", imc: tg.imc ?? "", resultado_operacional: tg.resultado_operacional ?? "" });
      const p = pr.data.find((x) => x.month === month);
      setPeriodStatus(p ? p.status : "OPEN");
    } finally { setLoading(false); }
  }, [period.from, period.to, month]);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const changeStatus = async (status) => {
    try { await api.post("/financial-periods", { month, status }); setPeriodStatus(status); toast.success(`Período ${month} agora está ${status}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const calcRequired = async () => {
    const d = parseDecimal(desired);
    if (isNaN(d)) { toast.error("Informe a meta de resultado"); return; }
    try {
      const r = await api.post("/finance/target-revenue", { date_from: period.from, date_to: period.to, desired_result: d });
      setReqResult(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTarget = async () => {
    try {
      await api.post("/financial-targets", {
        month,
        receita_liquida: parseDecimal(target.receita_liquida) || null,
        imc: parseDecimal(target.imc) || null,
        resultado_operacional: parseDecimal(target.resultado_operacional) || null,
      });
      toast.success("Meta salva"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const c = summary?.current;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Metas & Simulador</h1>
        <p className="text-muted-foreground mt-1">Referências gerenciais e simulação de cenários (não alteram dados reais)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4"><Calculator className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Faturamento necessário para meta de resultado</h3></div>
          <div className="mb-3"><PeriodFilter value={period} onChange={setPeriod} /></div>
          {loading || !c ? <Loader2 className="h-5 w-5 animate-spin text-accent" /> : (
            <div className="text-sm space-y-1 mb-4 text-muted-foreground">
              <div className="flex justify-between"><span>IMC do período</span><span className="font-num text-foreground">{formatPct(c.imc)}</span></div>
              <div className="flex justify-between"><span>Despesas fixas do período</span><span className="font-num text-foreground">{formatBRL(c.despesas_fixas)}</span></div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label>Meta de resultado (R$)</Label><Input type="text" inputMode="decimal" placeholder="5.000,00" value={desired} onChange={(e) => setDesired(e.target.value)} data-testid="target-desired-input" /></div>
            <Button onClick={calcRequired} data-testid="target-calc-button">Calcular</Button>
          </div>
          {reqResult && (
            <div className="mt-4 rounded-lg bg-secondary px-4 py-3" data-testid="target-result">
              <p className="section-label">Faturamento necessário</p>
              <p className="font-num text-2xl font-bold mt-1">{reqResult.calculavel ? formatBRL(reqResult.faturamento_necessario) : "N/A"}</p>
              {!reqResult.calculavel && <p className="text-xs text-amber-600 mt-1">Não é possível calcular: margem de contribuição do período é igual ou inferior a zero.</p>}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4"><Target className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Meta mensal</h3></div>
          <div className="space-y-3">
            <div><Label>Mês</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="target-month-input" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Receita Líq.</Label><Input type="text" inputMode="decimal" value={target.receita_liquida} onChange={(e) => setTarget({ ...target, receita_liquida: e.target.value })} className="h-8" /></div>
              <div><Label className="text-xs">IMC %</Label><Input type="text" inputMode="decimal" value={target.imc} onChange={(e) => setTarget({ ...target, imc: e.target.value })} className="h-8" /></div>
              <div><Label className="text-xs">Resultado</Label><Input type="text" inputMode="decimal" value={target.resultado_operacional} onChange={(e) => setTarget({ ...target, resultado_operacional: e.target.value })} className="h-8" /></div>
            </div>
            <Button onClick={saveTarget} className="w-full" data-testid="target-save-button">Salvar meta</Button>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <p className="section-label mb-2">Fechamento do período ({month})</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded font-medium ${periodStatus === "OPEN" ? "bg-emerald-100 text-emerald-700" : periodStatus === "CLOSED" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`} data-testid="period-status-badge">{periodStatus}</span>
              <Button size="sm" variant="outline" onClick={() => changeStatus("OPEN")} data-testid="period-open">Abrir</Button>
              <Button size="sm" variant="outline" onClick={() => changeStatus("CLOSED")} data-testid="period-close">Fechar</Button>
              <Button size="sm" variant="outline" onClick={() => changeStatus("LOCKED")} data-testid="period-lock">Bloquear</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Meses CLOSED ou LOCKED bloqueiam alterações de lançamentos naquela competência (trilha de auditoria mantida).</p>
          </div>
          {c && (
            <div className="mt-4 text-sm border-t border-border pt-3 space-y-1">
              <p className="section-label mb-1">Meta × Realizado (período selecionado)</p>
              <div className="flex justify-between"><span className="text-muted-foreground">Receita Líquida realizada</span><span className="font-num">{formatBRL(c.receita_liquida)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Resultado realizado</span><span className="font-num">{formatBRL(c.resultado_operacional)}</span></div>
            </div>
          )}
        </div>
      </div>

      <h2 className="font-display text-lg font-semibold mb-4">Simulador de cenários</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ScenarioCard name="Conservador" testid="scenario-conservador" />
        <ScenarioCard name="Base" testid="scenario-base" />
        <ScenarioCard name="Otimista" testid="scenario-otimista" />
      </div>
    </div>
  );
}
